/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Card, Grid, ICON_TROPHY_OUTLINE, Message,
  Skeleton,
  Spacing,
  Stack,
  Text,
  TextSkeleton,
  TypographyName,
} from '@doveit/bricks';
import Markdown from 'react-markdown';
import { Agent } from '../../../providers/api/dtos';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import * as styles from './ManagerRankWidget.style';
import AgentConversionRateSummaryCardSkeleton from '../../components/agent-conversion-rate-summary-card/AgentConversionRateSummaryCard.skeleton';
import useManagerRank from '../../hooks/use-manager-rank/useManagerRank';
import TeamLeaderboardPositionPreview from '../../components/team-leaderboard-position-preview/TeamLeaderboardPositionPreview';
import ManagerConversionRateSummaryCard from '../../components/manager-conversion-rate-summary-card/ManagerConversionRateSummaryCard';
import TeamLeaderboardPositionPreviewSkeleton from '../../components/team-leaderboard-position-preview/TeamLeaderboardPositionPreview.skeleton';
import { DescendantsStrategy } from '../../../domain/types';
import { getHygraphAssetUrl } from '../../../utils';

export interface ManagerRankWidgetProps {
  agentId: NonNullable<Agent['id']>,
}

export const LOAD_TEAM_RANK_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni sulla classifica agenti.';
export const LOAD_TEAM_GOAL_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni sulle metriche.';

export const ManagerRankWidget: React.FC<ManagerRankWidgetProps> = ({
  agentId,
}) => {
  const {
    data: managerRank,
    isLoading: isManagerRankLoading,
    error: managerRankError,
  } = useManagerRank(agentId);

  const { data: agent, isLoading: isAgentLoading, error: agentError } = useAgent(agentId, { descendantsStrategy: DescendantsStrategy.DIRECT });

  const teamLeaderboardItems = React.useMemo(() => {
    if (!managerRank || !agent) return [];

    const team = [...(agent.canManage ?? []), agent]; // re-add current agent to team

    return team
      .map((teamAgent) => {
        const rank = managerRank.teamRank.find((agentRank) => teamAgent.id === agentRank.agentId);
        if (rank) {
          return {
            ...rank,
            name: `${teamAgent.name.name} ${teamAgent.name.surname}`,
            picture: teamAgent.photoId ? getHygraphAssetUrl(teamAgent.photoId) : undefined,
          };
        } return undefined;
      })
      .filter((rank) => !!rank)
      .sort((a, b) => a.rank - b.rank);
  }, [managerRank, agent]);

  return (
    <Card aria-label="Classifica agenti team">
      <Card.Header
        title="Classifica agenti team"
        icon={{ path: ICON_TROPHY_OUTLINE }}
      />
      <Card.Content>
        <Spacing padding={[150, 0, 0]}>
          <Grid gutter={75}>
            {/** Rank */}
            <Grid.Unit size={{ MD: 1 / 2 }}>

              {(isManagerRankLoading || isAgentLoading) && (
                <Stack gap={300}>
                  <TextSkeleton count={1} typography={TypographyName.BODY} />

                  <div css={styles.rankPreview}>
                    <TeamLeaderboardPositionPreviewSkeleton />
                  </div>
                </Stack>
              )}

              {(managerRankError || agentError) && (
                <Message type="critical" message={LOAD_TEAM_RANK_ERROR_MESSAGE} />
              )}

              {(managerRank && agent) && (
                <Stack gap={300}>
                  <Text.Body>
                    <Markdown>
                      **Posizioni in classifica** dei consulenti e agenti del tuo team
                    </Markdown>
                  </Text.Body>

                  <div css={styles.rankPreview}>
                    <TeamLeaderboardPositionPreview
                      teamLeaderboardItems={teamLeaderboardItems}
                    />
                  </div>
                </Stack>
              )}
            </Grid.Unit>

            {/** CR Metrics */}
            <Grid.Unit size={{ MD: 1 / 2 }}>
              {(isManagerRankLoading) && (
                <Stack gap={300}>
                  <Skeleton height="1rem" width="4.5rem" />

                  <Stack gap={150}>
                    <AgentConversionRateSummaryCardSkeleton />
                    <AgentConversionRateSummaryCardSkeleton />
                  </Stack>
                </Stack>
              )}

              {(managerRankError) && (
                <Message type="critical" message={LOAD_TEAM_GOAL_ERROR_MESSAGE} />
              )}

              {managerRank && (
                <Stack gap={300}>
                  <Text.Body>
                    <strong>
                      Metriche
                    </strong>
                  </Text.Body>

                  <Stack gap={150}>
                    <ManagerConversionRateSummaryCard
                      cr="cr2"
                      metric={managerRank.conversions.cr2}
                    />
                    <ManagerConversionRateSummaryCard
                      cr="cr3"
                      metric={managerRank.conversions.cr3}
                    />
                  </Stack>
                </Stack>
              )}
            </Grid.Unit>
          </Grid>
        </Spacing>
      </Card.Content>
    </Card>
  );
};

export default ManagerRankWidget;
